import { Centrifuge } from 'centrifuge';
import ApiService from 'services/api/api.service';
import { GENERIC_NOTIFICATION } from 'constants/notifications';
import { WS_FULL_URL } from 'config/websocket.config';
import { formatPackageMessage } from 'services/base/websocket.utils';
import he from 'he';

export default class WebsocketService {
    static instance = null;

    resubscription = new Set(); // message listeners
    centrifugeSubscriptions = new Set(); // active Centrifuge subscriptions
    eventsListeners = new Map(); // event bus listeners { eventName => Set<listener> }

    static getInstance() {
        if (!WebsocketService.instance) {
            WebsocketService.instance = new WebsocketService();
        }
        return WebsocketService.instance;
    }

    static resetInstance() {
        if (WebsocketService.instance) {
            WebsocketService.instance.removeAllListeners();
            if (WebsocketService.instance.centrifuge) {
                WebsocketService.instance.centrifuge.disconnect();
            }
            WebsocketService.instance = null;
        }
    }


    constructor() {

        this.resubscription = new Set();
        this.centrifugeSubscriptions = new Set();
        this.eventsListeners = new Map();

        this.centrifuge = new Centrifuge(WS_FULL_URL);
        this.initEvents();
        this.centrifuge.connect();
        this.subscribeToGenericNotifications();
        this.subscribeToAllPackageStatuses();
        this.subscribeToMessage();
    }



    // ---------------------------
    // Centrifuge cleanup
    // ---------------------------
    removeAllListeners() {
        // 1. Centrifuge message listeners
        this.resubscription.forEach((listener) => this.centrifuge.removeListener('message', listener));
        this.resubscription.clear();

        // 2. Centrifuge subscriptions
        this.centrifugeSubscriptions.forEach((sub) => {
            sub.unsubscribe();
            sub.removeAllListeners();
        });
        this.centrifugeSubscriptions.clear();

        // 3. Event bus listeners
        this.eventsListeners.forEach((listeners, eventName) => {
            listeners.forEach((listener) => document.removeEventListener(eventName, listener));
        });
        this.eventsListeners.clear();
    }

    // ---------------------------
    // Event bus
    // ---------------------------
    events = {
        subscribe: (eventName, listener) => {
            document.addEventListener(eventName, listener);
            if (!this.eventsListeners.has(eventName)) this.eventsListeners.set(eventName, new Set());
            this.eventsListeners.get(eventName).add(listener);
        },

        unsubscribe: (eventName, listener) => {
            document.removeEventListener(eventName, listener);
            this.eventsListeners.get(eventName)?.delete(listener);
        },

        removeAllListeners: (eventName) => {
            const listeners = this.eventsListeners.get(eventName);
            if (listeners) {
                listeners.forEach((listener) => document.removeEventListener(eventName, listener));
                listeners.clear();
            }
        },

        publish: (eventName, data) => {
            if (data?.data) {
                data.data = this.normalizeIncomingData(data.data);
            }
            const event = new CustomEvent(eventName, { detail: data });
            document.dispatchEvent(event);
        }
    };


    // ---------------------------
    // Centrifuge subscriptions
    // ---------------------------
    subscribeToGenericNotifications() {
        const sub = this.centrifuge.newSubscription(GENERIC_NOTIFICATION.name);
        sub.subscribe();
        this.centrifugeSubscriptions.add(sub);
    }

    subscribeToAllPackageStatuses() {
        const sub = this.centrifuge.newSubscription('package.statuses');
        sub.subscribe();
        this.centrifugeSubscriptions.add(sub);
    }

    // ---------------------------
    // Подписка на message
    // ---------------------------
    subscribeToMessage() {

        const listener = (ctx) => {
            let { data } = ctx || {};
            if (!data) return;

            switch (data.messageType) {
                case 'PackageStateChanged':
                    this.handlePackageStateChanged(data);
                    break;

                case 'PackageBudgetUpdate':
                    break;

                default:
                    this.handleDefaultMessage(data, ctx);
            }
        };

        this.resubscription.add(listener);
        this.centrifuge.addListener('message', listener);

        return () => {
            this.resubscription.delete(listener);
            this.centrifuge.removeListener('message', listener);
        };
    }

    // ---------------------------
    // Обработчики сообщений
    // ---------------------------
    handlePackageStateChanged(data) {
        const notificationData = formatPackageMessage(data);
        if (!notificationData) return;

        this.events.publish(GENERIC_NOTIFICATION.event, {
            data: {
                ...notificationData,
                type: GENERIC_NOTIFICATION.event
            }
        });
    }

    handleDefaultMessage(data, ctx) {
        if (data?.type === 'events.subscription.changed') {
            ApiService.getData('/platformendpoints/profile/general')
                .then(() => { })
                .catch(() => {
                    window.location.href = '/price/logout';
                });
            return;
        }

        if (data?.type && (data.type === GENERIC_NOTIFICATION.name || GENERIC_NOTIFICATION.types.includes(data.type))) {
            this.events.publish(GENERIC_NOTIFICATION.event, ctx);
        }
    }

    // ---------------------------
    // Events init
    // ---------------------------
    initEvents() {
        this.centrifuge.on('connected', () => {
            // при reconnect пересоздаем все listeners
            for (const listener of this.resubscription) {
                this.centrifuge.removeListener('message', listener);
                this.centrifuge.addListener('message', listener);
            }
        });
    }

    normalizeIncomingData(data) {
        if (!data) return data;

        const normalized = { ...data };

        Object.keys(normalized).forEach((key) => {
            if (typeof normalized[key] === 'string') {
                normalized[key] = he.decode(normalized[key]);
            }
        });

        return normalized;
    }
}
